import { useState } from 'react';
import { useT } from '@/i18n/runtime';
import { useSessionStore } from '@/store/sessionStore';
import type { StepProps } from '../types';
import { BrandMark } from '../components/BrandMark';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';

/** SCREEN 01 · WELCOME — branded, reassuring first touch (the white-label slot). */
export function Welcome({ tenant }: StepProps): JSX.Element {
  const reference = useSessionStore((s) => s.session.reference) ?? 'VCIP-7F3A-22K1';
  // 19-Aug: the trust chip names the SESSION's regime (IFSCA journeys said "RBI V-CIP").
  const regime = useSessionStore((s) => s.session.regime);
  const advance = useSessionStore((s) => s.completeCurrentAndAdvance);
  const { t } = useT();
  const [needsName, setNeedsName] = useState(() => !tenant.branding.logoUrl);

  return (
    <div className="app-view center">
      <div className="center-col">
        {/* ★ WHITE-LABEL LOGO SLOT — tenant mark + name reskin via --brand ★
            The name text renders whenever the mark still needs it (monogram
            fallback, broken logo, or a symbol-shaped logo — BrandMark
            measures and reports); a wordmark logo carries the name itself.
            The platform credit is the shell's small "Powered by KYC Hub"
            footer — this line repeats a credit only when the tenant's SERVED
            config carries a cobrand of their own. */}
        <div className="lockup" style={{ flexDirection: 'column', gap: 12 }}>
          <BrandMark tenant={tenant} size="lg" onNeedsName={setNeedsName} />
          <div>
            {needsName ? (
              <div className="brand-name" style={{ fontSize: 'var(--fs-h3)' }}>
                {tenant.name}
              </div>
            ) : null}
            <div className="cobrand" style={{ marginTop: 4 }}>
              {tenant.cobrand ? `${tenant.cobrand} · V-CIP` : 'V-CIP'}
            </div>
          </div>
        </div>

        <div className="stack-sm">
          <div className="h-title" style={{ whiteSpace: 'pre-line' }}>
            {t('droom.welcome.title')}
          </div>
          <p className="sub" style={{ maxWidth: '30ch', margin: '0 auto' }}>
            {t('droom.welcome.sub')}
          </p>
        </div>
      </div>

      <div className="spacer" />

      <div className="stack" style={{ marginTop: 18 }}>
        <div className="trust-row">
          <div className="trust">
            <div className="ic">
              <Icon name="shield-check" />
            </div>
            <div className="l">{t(regime === 'IFSCA' ? 'droom.welcome.trust.ifsca.l' : 'droom.welcome.trust.rbi.l')}</div>
            <div className="s">{t('droom.welcome.trust.rbi.s')}</div>
          </div>
          <div className="trust">
            <div className="ic">
              <Icon name="lock" />
            </div>
            <div className="l">{t('droom.welcome.trust.enc.l')}</div>
            <div className="s">{t('droom.welcome.trust.enc.s')}</div>
          </div>
          <div className="trust">
            <div className="ic">
              <Icon name="clock" />
            </div>
            <div className="l">{t('droom.welcome.trust.time.l')}</div>
            <div className="s">{t('droom.welcome.trust.time.s')}</div>
          </div>
        </div>

        <Button onClick={advance}>{t('droom.welcome.cta')}</Button>

        <div className="ref-chip">
          <span>{t('droom.welcome.ref')}</span>
          <b className="mono">{reference}</b>
        </div>
      </div>
    </div>
  );
}
